import { useEffect, useMemo, useState } from "react";
import { geoPath } from "d3-geo";
import { feature } from "topojson-client";
import us from "us-atlas/states-albers-10m.json";
import { Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const shapes = feature(us, us.objects.states).features;
const path = geoPath();
const COLOR = { both: "#10b981", one: "#fbbf24", none: "#f87171" };
const level = (r) => (r.cpa && r.attorney ? "both" : r.cpa || r.attorney ? "one" : "none");

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-500">
      {[["none", "No CPA or attorney"], ["one", "Missing one"], ["both", "CPA + attorney"]].map(([k, l]) => <span key={k} className="flex items-center gap-1.5"><span className="h-2.5 w-5 rounded-sm" style={{ background: COLOR[k] }} />{l}</span>)}
      <span className="ml-auto">Click a state for details</span>
    </div>
  );
}

function GapList({ title, states, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
      <div className="font-mono text-xs uppercase tracking-[0.18em] text-red-600">{title} · {states.length}</div>
      <p className="text-xs text-slate-600 mt-2 leading-relaxed max-h-28 overflow-y-auto">{states.join(", ") || "None — fully covered"}</p>
    </div>
  );
}

export function CoverageMap() {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(null);
  useEffect(() => { api.get("/admin/pro-coverage").then((r) => setD(r.data)); }, []);
  const byName = useMemo(() => Object.fromEntries((d?.states || []).map((r) => [r.state, r])), [d]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  const sel = pick && byName[pick];
  return (
    <div className="grid lg:grid-cols-12 gap-4" data-testid="coverage-map">
      <div className="lg:col-span-8 bg-white border border-slate-200 rounded-lg p-4">
        <svg viewBox="0 0 975 610" className="w-full h-auto">
          {shapes.map((s) => {
            const name = s.properties.name;
            const r = byName[name] || { cpa: 0, attorney: 0, editor: 0, pending: 0 };
            return (
              <path key={s.id} d={path(s)} fill={pick === name ? "#0f172a" : COLOR[level(r)]} stroke="#fff" strokeWidth={1} className="cursor-pointer transition-opacity hover:opacity-80"
                onClick={() => setPick(pick === name ? null : name)} data-testid={`coverage-state-${name.replace(/ /g, "-").toLowerCase()}`}>
                <title>{`${name}: ${r.cpa} CPA · ${r.attorney} attorney · ${r.editor} editor${r.pending ? ` · ${r.pending} pending` : ""}`}</title>
              </path>
            );
          })}
        </svg>
        <Legend />
      </div>
      <div className="lg:col-span-4 space-y-3">
        <div className="bg-slate-900 text-white rounded-lg p-4" data-testid="coverage-summary">
          <div className="font-display text-3xl font-extrabold">{d.full}<span className="text-base text-slate-400 font-normal"> / {d.states.length} states fully covered</span></div>
          {d.unset > 0 && <p className="text-xs text-amber-300 mt-2" data-testid="coverage-unset">{d.unset} active pro{d.unset === 1 ? " has" : "s have"} no licensed state — set it under Professional accounts.</p>}
        </div>
        {sel && (
          <div className="bg-white border border-slate-900 rounded-lg p-4 text-sm" data-testid="coverage-state-detail">
            <div className="flex justify-between items-center"><b className="text-slate-900">{sel.state}</b><button onClick={() => setPick(null)} data-testid="coverage-detail-close"><X className="h-4 w-4 text-slate-400" /></button></div>
            <div className="mt-2 grid grid-cols-2 gap-1 text-slate-600"><span>CPAs: <b>{sel.cpa}</b></span><span>Attorneys: <b>{sel.attorney}</b></span><span>Editors: <b>{sel.editor}</b></span><span>Pending apps: <b>{sel.pending}</b></span></div>
          </div>
        )}
        <GapList title="No CPA" states={d.no_cpa} testid="coverage-no-cpa" />
        <GapList title="No attorney" states={d.no_attorney} testid="coverage-no-attorney" />
      </div>
    </div>
  );
}

export function ReferralBoard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/referrals").then((r) => setRows(r.data)); }, []);
  if (!rows) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 mt-4" data-testid="referral-board">
      <div className="px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-emerald-700">Top recruiters</div>
      {rows.length === 0 && <p className="px-5 py-4 text-sm text-slate-500" data-testid="referral-board-empty">No referrals yet. Pros find their invite link in the pro portal.</p>}
      {rows.map((r) => <div key={r.user_id} className="px-5 py-3 flex text-sm" data-testid={`referral-row-${r.user_id}`}><span className="flex-1 text-slate-900">{r.name}</span><span className="text-slate-500">{r.invited} invited · <b className="text-emerald-700">{r.approved} approved</b></span></div>)}
    </div>
  );
}
